// src/pages/Platform/Chatbot/ChatbotBuilder/KnowledgeBase/Website/WebsiteTab.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, TextField, Button, Alert, CircularProgress,
    LinearProgress, Tabs, Tab, Tooltip, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Globe, Link, Plus, X, AlertCircle } from 'react-feather';
import axios from 'axios';
import logger from '../../../../../../utils/logger';

import { auth } from '../../../../../../firebase/config/config';
import SourceList from './SourceList';
import TrainedWebsites from './TrainedWebsites';
import KnowledgeBaseOverview from '../KnowledgeBaseOverview';
import UniversalKnowledgeBasePanel from '../components/UniversalKnowledgeBasePanel';

// --- Styled Components ---

const SearchContainer = styled(Box)(() => ({
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
}));

const StyledTextField = styled(TextField)(() => ({
    flex: 1,
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: '#f8fafc',
        '& fieldset': { borderColor: '#e2e8f0' },
        '&:hover fieldset': { borderColor: '#cbd5e1' },
        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    },
}));

const TrainButton = styled(Button)(() => ({
    width: '100%',
    backgroundColor: '#000000',
    color: '#ffffff',
    padding: '12px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: '24px',
    '&:hover': { backgroundColor: '#333333' },
    '&:disabled': {
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
    },
}));

const UrlPill = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    marginBottom: '6px',
    '&:last-child': { marginBottom: 0 },
}));

// ─── Mode Toggle ──────────────────────────────────────────────────────────────

const MODE_SCAN   = 'scan';
const MODE_MANUAL = 'manual';

function WebsiteTab({ selectedChatbotId }) {
    const [mode, setMode] = useState(MODE_SCAN);

    // --- Scan mode state ---
    const [url, setUrl] = useState('');
    const [scannedLinks, setScannedLinks] = useState([]);
    const [selectedLinks, setSelectedLinks] = useState(new Set());
    const [isScanning, setIsScanning] = useState(false);

    // --- Manual mode state ---
    const [manualInput, setManualInput] = useState('');
    const [manualUrls, setManualUrls] = useState([]); // confirmed list
    const [manualInputError, setManualInputError] = useState('');

    // --- Shared state ---
    const [isTraining, setIsTraining] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [trainedWebsites, setTrainedWebsites] = useState([]);
    const [crawlProgress, setCrawlProgress] = useState(null);
    const pollingRef = useRef(null);
    const lastPagesCrawledRef = useRef(0);

    // Fetch existing trained websites
    const fetchTrainedWebsites = useCallback(async () => {
        if (!selectedChatbotId) return;
        try {
            const response = await axios.get(`/api/chatbot/${selectedChatbotId}/files`);
            const files = response.data.files || [];
            const websites = files.filter(file =>
                file.source === 'website' ||
                (!file.source && file.mime_type === 'text/markdown' &&
                    (file.original_name.startsWith('http') || file.original_name.startsWith('www')))
            );
            setTrainedWebsites(websites);
        } catch (err) {
            logger.error('Error fetching trained websites:', err);
        }
    }, [selectedChatbotId]);

    useEffect(() => { fetchTrainedWebsites(); }, [fetchTrainedWebsites]);

    // ── Scan mode handlers ────────────────────────────────────────────────────

    const handleScan = async () => {
        if (!url) return;
        setIsScanning(true);
        setError(null);
        setSuccessMessage(null);
        setScannedLinks([]);
        setSelectedLinks(new Set());
        try {
            const response = await axios.post('/api/files/crawl/scan', { url });
            const links = response.data.urls || [];
            setScannedLinks(links);
            setSelectedLinks(new Set(links)); // auto-select all
        } catch (err) {
            logger.error('Error scanning URL:', err);
            setError(err?.response?.data?.details || 'Failed to scan URL. Please check if the website is accessible.');
        } finally {
            setIsScanning(false);
        }
    };

    const handleScanTrain = async () => {
        if (selectedLinks.size === 0) return;
        setIsTraining(true);
        setError(null);
        setSuccessMessage(null);
        setCrawlProgress(null);
        try {
            const token = await auth.currentUser?.getIdToken();
            const response = await axios.post('/api/files/crawl/train', {
                urls: Array.from(selectedLinks),
                chatbotId: selectedChatbotId
            }, {
                headers: {
                    'x-user-id': auth.currentUser?.uid,
                    'Authorization': `Bearer ${token}`,
                }
            });
            setSuccessMessage('Training started! Processing your selected pages...');
            startProgressPolling(response.data.crawlId);
        } catch (err) {
            logger.error('Error starting training:', err);
            setError(err?.response?.data?.error || 'Failed to start training. Please try again.');
            setIsTraining(false);
        }
    };

    const toggleLink = (link) => {
        const next = new Set(selectedLinks);
        next.has(link) ? next.delete(link) : next.add(link);
        setSelectedLinks(next);
    };

    const toggleAll = () => {
        setSelectedLinks(
            selectedLinks.size === scannedLinks.length ? new Set() : new Set(scannedLinks)
        );
    };

    // ── Manual mode handlers ──────────────────────────────────────────────────

    const addManualUrl = () => {
        const raw = manualInput.trim();
        if (!raw) return;

        // Basic URL validation
        try {
            const normalised = new URL(raw.startsWith('http') ? raw : `https://${raw}`).toString();
            if (manualUrls.includes(normalised)) {
                setManualInputError('This URL is already in the list.');
                return;
            }
            setManualUrls(prev => [...prev, normalised]);
            setManualInput('');
            setManualInputError('');
        } catch {
            setManualInputError('Please enter a valid URL (e.g. https://example.com/page)');
        }
    };

    const removeManualUrl = (urlToRemove) => {
        setManualUrls(prev => prev.filter(u => u !== urlToRemove));
    };

    const handleManualKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addManualUrl(); }
    };

    const handleManualTrain = async () => {
        if (manualUrls.length === 0) return;
        setIsTraining(true);
        setError(null);
        setSuccessMessage(null);
        setCrawlProgress(null);
        try {
            const token = await auth.currentUser?.getIdToken();
            const response = await axios.post('/api/files/crawl/add-urls', {
                urls: manualUrls,
                chatbotId: selectedChatbotId
            }, {
                headers: {
                    'x-user-id': auth.currentUser?.uid,
                    'Authorization': `Bearer ${token}`,
                }
            });
            setSuccessMessage(`Processing ${manualUrls.length} URL${manualUrls.length > 1 ? 's' : ''}...`);
            setManualUrls([]); // clear the list
            startProgressPolling(response.data.crawlId);
        } catch (err) {
            logger.error('Error starting manual URL ingestion:', err);
            setError(err?.response?.data?.error || 'Failed to start ingestion. Please try again.');
            setIsTraining(false);
        }
    };

    // ── Shared helpers ────────────────────────────────────────────────────────

    const startProgressPolling = useCallback((id) => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        lastPagesCrawledRef.current = 0;

        pollingRef.current = setInterval(async () => {
            try {
                const { data: status } = await axios.get(`/api/files/crawl/status/${id}`);
                setCrawlProgress(status);
                if (status.pagesCrawled > lastPagesCrawledRef.current) {
                    lastPagesCrawledRef.current = status.pagesCrawled;
                    fetchTrainedWebsites();
                }

                const done =
                    status.status === 'done' ||
                    status.status === 'completed' ||
                    status.status === 'failed';

                if (done) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    setIsTraining(false);
                    if (status.status === 'failed' || !status.pagesCrawled) {
                        setError(status.pagesCrawled
                            ? `Website training stopped after adding ${status.pagesCrawled} pages. ${status.pagesFailed || 0} failed. You can retry below.`
                            : `Website training finished without adding pages. ${status.pagesSkipped || 0} skipped, ${status.pagesFailed || 0} failed. Check the URL or add specific pages below.`);
                        setSuccessMessage(null);
                    } else {
                        setError(null);
                        setSuccessMessage(`Done! ${status.pagesCrawled} page${status.pagesCrawled !== 1 ? 's' : ''} added to the knowledge base.`);
                    }
                    fetchTrainedWebsites();
                }
            } catch (err) {
                logger.error('Error polling crawl status:', err);
            }
        }, 3000);
    }, [fetchTrainedWebsites]);

    useEffect(() => {
        if (!selectedChatbotId) return;
        let cancelled = false;
        const loadLatestCrawl = async () => {
            try {
                const token = await auth.currentUser?.getIdToken();
                const { data: status } = await axios.get(`/api/files/crawl/latest/${selectedChatbotId}`, {
                    headers: { 'x-user-id': auth.currentUser?.uid, 'Authorization': `Bearer ${token}` },
                });
                if (cancelled || !status.crawlId) return;
                setCrawlProgress(status);
                if (status.status === 'running') {
                    setIsTraining(true);
                    setSuccessMessage('Website training is running. Pages will appear here as they finish.');
                    startProgressPolling(status.crawlId);
                } else if (status.status === 'failed' || !status.pagesCrawled) {
                    setError(status.pagesCrawled
                        ? `Website training stopped after adding ${status.pagesCrawled} pages. ${status.pagesFailed || 0} failed. You can retry below.`
                        : `Website training finished without adding pages. ${status.pagesSkipped || 0} skipped, ${status.pagesFailed || 0} failed. Check the URL or add specific pages below.`);
                }
            } catch (err) {
                logger.error('Error loading latest crawl status:', err);
            }
        };
        loadLatestCrawl();
        return () => { cancelled = true; if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [selectedChatbotId, startProgressPolling]);

    useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

    const handleDeleteWebsite = async (website) => {
        if (!window.confirm(`Are you sure you want to delete ${website.original_name}?`)) return;
        try {
            await axios.delete(`/api/files/${website.id}`);
            fetchTrainedWebsites();
        } catch (err) {
            logger.error('Error deleting website:', err);
            setError('Failed to delete website.');
        }
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    const topContent = (
        <>
            {/* Mode toggle tabs */}
            <Tabs
                value={mode}
                onChange={(_, v) => { setMode(v); setError(null); setSuccessMessage(null); }}
                sx={{
                    mb: 2,
                    minHeight: 36,
                    '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: 2 },
                    '& .MuiTab-root': {
                        textTransform: 'none', fontWeight: 500, fontSize: '14px',
                        minHeight: 36, padding: '6px 16px', color: '#64748b',
                    },
                    '& .Mui-selected': { color: '#1e293b !important', fontWeight: 600 },
                }}
            >
                <Tab
                    value={MODE_SCAN}
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Globe size={14} /> Scan Website
                        </Box>
                    }
                />
                <Tab
                    value={MODE_MANUAL}
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Link size={14} /> Add URLs
                        </Box>
                    }
                />
            </Tabs>

            {/* Scan mode input */}
            {mode === MODE_SCAN && (
                <SearchContainer>
                    <StyledTextField
                        placeholder="https://example.com"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                        size="small"
                        InputProps={{
                            startAdornment: <Globe size={18} color="#94a3b8" style={{ marginRight: 8 }} />,
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleScan}
                        disabled={isScanning || !url}
                        sx={{
                            backgroundColor: '#0f172a', textTransform: 'none',
                            borderRadius: '8px', px: 3, '&:hover': { backgroundColor: '#1e293b' }
                        }}
                    >
                        {isScanning ? <CircularProgress size={20} color="inherit" /> : 'Scan'}
                    </Button>
                </SearchContainer>
            )}

            {/* Manual URL input */}
            {mode === MODE_MANUAL && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 1 }}>
                        Add specific pages one by one — useful for SPAs or pages not discovered by scanning.
                    </Typography>
                    <SearchContainer sx={{ mb: 0 }}>
                        <StyledTextField
                            placeholder="https://example.com/about"
                            value={manualInput}
                            onChange={(e) => { setManualInput(e.target.value); setManualInputError(''); }}
                            onKeyDown={handleManualKeyDown}
                            size="small"
                            error={!!manualInputError}
                            InputProps={{
                                startAdornment: <Link size={18} color="#94a3b8" style={{ marginRight: 8 }} />,
                            }}
                        />
                        <Tooltip title="Add URL (or press Enter)">
                            <Button
                                variant="contained"
                                onClick={addManualUrl}
                                disabled={!manualInput.trim()}
                                sx={{
                                    backgroundColor: '#0f172a', textTransform: 'none',
                                    borderRadius: '8px', px: 2, minWidth: 44,
                                    '&:hover': { backgroundColor: '#1e293b' }
                                }}
                            >
                                <Plus size={18} />
                            </Button>
                        </Tooltip>
                    </SearchContainer>
                    {manualInputError && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                            <AlertCircle size={13} color="#ef4444" />
                            <Typography sx={{ fontSize: '12px', color: '#ef4444' }}>{manualInputError}</Typography>
                        </Box>
                    )}
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMessage && <Alert severity={crawlProgress?.status === 'running' ? 'info' : 'success'} sx={{ mb: 2 }}>{successMessage}</Alert>}
        </>
    );

    const rightPanelContent = (
        <KnowledgeBaseOverview selectedChatbotId={selectedChatbotId}>
            {/* Scan mode: show when links are scanned */}
            {mode === MODE_SCAN && scannedLinks.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '14px', color: '#64748b' }}>Links Found</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{scannedLinks.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontSize: '14px', color: '#64748b' }}>Selected</Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{selectedLinks.size}</Typography>
                    </Box>
                    <TrainButton onClick={handleScanTrain} disabled={selectedLinks.size === 0 || isTraining}>
                        {isTraining ? 'Training...' : 'Train Selected Links'}
                    </TrainButton>
                    {crawlProgress && crawlProgress.pagesTotal > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Processing pages...</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                    {crawlProgress.pagesCrawled || 0} / {crawlProgress.pagesTotal}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(((crawlProgress.pagesCrawled || 0) / crawlProgress.pagesTotal) * 100, 100)}
                                sx={{
                                    height: 8, borderRadius: 4, backgroundColor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#10b981' }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {/* Manual mode: show queued URL count + train button */}
            {mode === MODE_MANUAL && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontSize: '14px', color: '#64748b' }}>URLs Queued</Typography>
                        <Chip
                            label={manualUrls.length}
                            size="small"
                            sx={{ backgroundColor: manualUrls.length > 0 ? '#3b82f6' : '#e2e8f0', color: manualUrls.length > 0 ? '#fff' : '#94a3b8', fontWeight: 600 }}
                        />
                    </Box>
                    <TrainButton
                        onClick={handleManualTrain}
                        disabled={manualUrls.length === 0 || isTraining}
                    >
                        {isTraining ? 'Ingesting...' : `Add ${manualUrls.length > 0 ? manualUrls.length : ''} URL${manualUrls.length !== 1 ? 's' : ''} to Knowledge Base`}
                    </TrainButton>
                    {crawlProgress && crawlProgress.pagesTotal > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Processing...</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                    {crawlProgress.pagesCrawled || 0} / {crawlProgress.pagesTotal}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(((crawlProgress.pagesCrawled || 0) / crawlProgress.pagesTotal) * 100, 100)}
                                sx={{
                                    height: 8, borderRadius: 4, backgroundColor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#10b981' }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </KnowledgeBaseOverview>
    );

    return (
        <UniversalKnowledgeBasePanel
            title="Website"
            subtitle="Crawl web pages or add specific URLs to your knowledge base."
            topContent={topContent}
            rightPanelContent={rightPanelContent}
        >
            <Box sx={{ p: 3, pt: 0 }}>
                {/* Scan mode: scanned links or trained websites */}
                {mode === MODE_SCAN && (
                    scannedLinks.length > 0 ? (
                        <SourceList
                            links={scannedLinks}
                            selectedLinks={selectedLinks}
                            onToggle={toggleLink}
                            onToggleAll={toggleAll}
                        />
                    ) : (
                        <TrainedWebsites
                            websites={trainedWebsites}
                            onDelete={handleDeleteWebsite}
                        />
                    )
                )}

                {/* Manual mode: show queued URL pills + trained websites below */}
                {mode === MODE_MANUAL && (
                    <>
                        {manualUrls.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 1.5, fontWeight: 500 }}>
                                    Queued ({manualUrls.length})
                                </Typography>
                                {manualUrls.map((u) => (
                                    <UrlPill key={u}>
                                        <Typography
                                            sx={{
                                                fontSize: '13px', color: '#1e293b',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                flex: 1,
                                            }}
                                            title={u}
                                        >
                                            {u}
                                        </Typography>
                                        <Box
                                            component="button"
                                            onClick={() => removeManualUrl(u)}
                                            sx={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', p: 0, flexShrink: 0,
                                                color: '#94a3b8', '&:hover': { color: '#ef4444' },
                                            }}
                                        >
                                            <X size={14} />
                                        </Box>
                                    </UrlPill>
                                ))}
                            </Box>
                        )}
                        <TrainedWebsites
                            websites={trainedWebsites}
                            onDelete={handleDeleteWebsite}
                        />
                    </>
                )}
            </Box>
        </UniversalKnowledgeBasePanel>
    );
}

export default WebsiteTab;
